import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box, Card, CircularProgress, Collapse, Divider, Grid, IconButton, Stack, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  ExpandLessRounded as ExpandLessIcon,
  ExpandMoreRounded as ExpandMoreIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';
import { httpClient } from '@/utils/httpClient';
import type { IFieldMappingModel } from '@/types';
import { Metric, MetricRow } from './Metric';
import { asImageSrc } from './testUtils';

/* ── Residence data ── */

export interface GeoData {
  municipality?: string; municipalityCode?: string;
  province?: string; provinceCode?: string;
  region?: string; regionCode?: string;
  postalCode?: string;
  latitude?: string; longitude?: string;
  nationalityAlpha2?: string; nationalityAlpha3?: string;
}

interface V2GeoData {
  PostalCode?: string; Region?: string; RegionCode?: string;
  Province?: string; ProvinceCode?: string;
  Municipality?: string; MunicipalityCode?: string;
  City?: string; Latitude?: string; Longitude?: string;
  NationalityAlpha2?: string; NationalityAlpha3?: string;
}

/** Extract geo data. v2 exposes enrichment fields in a dedicated GeoData object;
 * document-native fields remain in mapped Data; raw OCR fields use NORM_GEO_* prefix. */
export const extractGeoData = (
  v2Geo: V2GeoData | null,
  mappedData: Record<string, string> | null,
  rawKV: Record<string, string> | null,
): GeoData | null => {
  const geo: GeoData = {};
  // v2 GeoData object has priority (enrichment output)
  if (v2Geo) {
    geo.municipality = v2Geo.Municipality;
    geo.municipalityCode = v2Geo.MunicipalityCode;
    geo.province = v2Geo.Province;
    geo.provinceCode = v2Geo.ProvinceCode;
    geo.region = v2Geo.Region;
    geo.regionCode = v2Geo.RegionCode;
    geo.postalCode = v2Geo.PostalCode;
    geo.latitude = v2Geo.Latitude;
    geo.longitude = v2Geo.Longitude;
    geo.nationalityAlpha2 = v2Geo.NationalityAlpha2;
    geo.nationalityAlpha3 = v2Geo.NationalityAlpha3;
  }
  // Document-native geo fields (e.g., province printed on the ID) stay in mapped root
  if (mappedData) {
    const m = mappedData;
    geo.municipality = geo.municipality || m.Municipality || m.GeoMunicipality;
    geo.municipalityCode = geo.municipalityCode || m.MunicipalityCode || m.GeoMunicipalityCode;
    geo.province = geo.province || m.ProvinceOfResidence || m.GeoProvince;
    geo.provinceCode = geo.provinceCode || m.ProvinceCode || m.GeoProvinceCode;
    geo.region = geo.region || m.Region || m.GeoRegion;
    geo.regionCode = geo.regionCode || m.RegionCode || m.GeoRegionCode;
    geo.postalCode = geo.postalCode || m.PostalCode || m.GeoPostalCode;
    geo.latitude = geo.latitude || m.Latitude || m.GeoLatitude;
    geo.longitude = geo.longitude || m.Longitude || m.GeoLongitude;
    geo.nationalityAlpha2 = geo.nationalityAlpha2 || m.NationalityAlpha2;
    geo.nationalityAlpha3 = geo.nationalityAlpha3 || m.NationalityAlpha3;
  }
  // Fill gaps from raw NORM_GEO_* fields
  if (rawKV) {
    const g = (key: string) => rawKV[`NORM_GEO_${key}`];
    geo.municipality = geo.municipality || g('Municipality');
    geo.municipalityCode = geo.municipalityCode || g('MunicipalityCode');
    geo.province = geo.province || g('Province');
    geo.provinceCode = geo.provinceCode || g('ProvinceCode');
    geo.region = geo.region || g('Region');
    geo.regionCode = geo.regionCode || g('RegionCode');
    geo.postalCode = geo.postalCode || g('PostalCode');
    geo.latitude = geo.latitude || g('Latitude');
    geo.longitude = geo.longitude || g('Longitude');
    geo.nationalityAlpha2 = geo.nationalityAlpha2 || g('NationalityAlpha2');
    geo.nationalityAlpha3 = geo.nationalityAlpha3 || g('NationalityAlpha3');
  }
  const hasAny = Object.values(geo).some(v => v);
  return hasAny ? geo : null;
};

const hasWebGL = (() => {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl') || c.getContext('experimental-webgl'));
  } catch { return false; }
})();

const cardSx = { display: 'flex', flexDirection: 'column', minWidth: 0 } as const;

const GeoCard: React.FC<{ geo: GeoData | null }> = ({ geo }) => {
  const t = useTranslate();
  const theme = useTheme();
  const hasCoords = geo?.latitude && geo?.longitude;
  const locationLine = geo ? [
    geo.municipality,
    geo.province && geo.province !== geo.municipality ? geo.province : null,
    geo.region && geo.region !== geo.province ? geo.region : null,
  ].filter(Boolean).join(' · ') : '';
  const codes = geo ? [
    geo.postalCode ? t('screens.testPage.postalCode', { code: geo.postalCode }) : null,
    geo.provinceCode,
    geo.regionCode,
    geo.nationalityAlpha2 ? `${geo.nationalityAlpha2}${geo.nationalityAlpha3 ? ` · ${geo.nationalityAlpha3}` : ''}` : null,
  ].filter(Boolean).join(' · ') : '';

  return (
    <Card sx={cardSx}>
      {hasCoords && hasWebGL && (
        <Box sx={{ flex: 1, minHeight: 140, position: 'relative', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <iframe
            title={t('screens.testPage.mapTitle')}
            width="100%" height="100%"
            style={{ border: 0, display: 'block', position: 'absolute', inset: 0 }}
            loading="lazy"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(geo!.longitude) - 0.02},${Number(geo!.latitude) - 0.015},${Number(geo!.longitude) + 0.02},${Number(geo!.latitude) + 0.015}&layer=mapnik&marker=${geo!.latitude},${geo!.longitude}`}
          />
        </Box>
      )}
      <Stack spacing={0.75} sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <PlaceIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle2">{locationLine || t('screens.testPage.residence')}</Typography>
        </Box>
        {!geo && (
          <Typography variant="body2" color="text.secondary">{t('screens.testPage.noResidence')}</Typography>
        )}
        {codes && <Typography variant="body2" color="text.secondary">{codes}</Typography>}
        {hasCoords && (
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: theme.custom.text.mono }}>
            {geo!.latitude}, {geo!.longitude}
          </Typography>
        )}
      </Stack>
    </Card>
  );
};

/* ── The field mappings configured for the recognised document ── */

const DocumentMappingCard: React.FC<{
  docTypeCandidates: Array<string | undefined>;
  nationalityCandidates: Array<string | undefined>;
}> = ({ docTypeCandidates, nationalityCandidates }) => {
  const t = useTranslate();
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [mappings, setMappings] = useState<IFieldMappingModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  // Normalise: lowercase, collapse any non-alphanumeric run to a single space, trim.
  // The OCR adds noise the configured name doesn't have ("#4", " Side B"), so we match
  // when the configured name equals OR is a token-aligned prefix of any candidate.
  const norm = (s: string | undefined) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const docTypeKeys = Array.from(new Set(docTypeCandidates.map(norm).filter(Boolean)));
  const natKeys = Array.from(new Set(nationalityCandidates.map(norm).filter(Boolean)));
  const hasDocType = docTypeKeys.length > 0;

  useEffect(() => {
    if (fetched || !hasDocType) return;
    setLoading(true);
    httpClient.get<{ data: IFieldMappingModel[] }>('/admin/fields-mapping/GetMappingFields?draw=1&start=0&length=500')
      .then(res => {
        const all = res.data?.data || [];
        const matchesDocType = (m: IFieldMappingModel) => {
          const configured = norm(m.documentTypeResponse?.name);
          if (!configured) return false;
          return docTypeKeys.some(k => k === configured || k.startsWith(configured + ' '));
        };
        const matchesNationality = (m: IFieldMappingModel) => {
          if (natKeys.length === 0) return true;
          const configured = norm(m.nationalityResponse?.name);
          return !configured || natKeys.includes(configured);
        };
        const strict = all.filter(m => matchesDocType(m) && matchesNationality(m));
        // Fallback: if nationality data was missing or did not match anything, show all
        // mappings for the matched document type. The doc-type match is now strict
        // (token-aligned prefix), so this no longer over-counts unrelated documents.
        setMappings(strict.length > 0 ? strict : all.filter(matchesDocType));
      })
      .catch(() => setMappings([]))
      .finally(() => { setLoading(false); setFetched(true); });
  }, [fetched, hasDocType, docTypeKeys.join('|'), natKeys.join('|')]);

  const totalFields = mappings.reduce((sum, m) => sum + (m.mappingFields?.length || 0), 0);
  const toggleLabel = t(open ? 'screens.testPage.hideMappings' : 'screens.testPage.showMappings');

  return (
    <Card sx={cardSx}>
      <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>{t('screens.testPage.mappings')}</Typography>
        {fetched && !loading && (
          <Typography variant="body2" sx={{ color: totalFields > 0 ? 'text.secondary' : 'warning.dark' }}>
            {t('screens.testPage.mappingFields', { smart_count: totalFields })}
          </Typography>
        )}
        <Tooltip title={toggleLabel}>
          <IconButton aria-label={toggleLabel} aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={open}>
        <Divider />
        <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} aria-label={t('ra.page.loading')} />
            </Box>
          )}
          {!hasDocType && (
            <Typography variant="body2" color="text.secondary">{t('screens.testPage.noDocumentType')}</Typography>
          )}
          {hasDocType && fetched && mappings.length === 0 && (
            <Typography variant="body2" color="text.secondary">{t('screens.testPage.noMappings')}</Typography>
          )}
          {mappings.map((m, mi) => (
            <Box key={mi}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {[m.documentTypeResponse?.name, m.nationalityResponse?.name, m.documentSide].filter(Boolean).join(' · ')}
              </Typography>
              <Stack spacing={0.25}>
                {m.mappingFields?.map((f, fi) => {
                  const sources = f.kycMappingField?.map(kf => {
                    if (kf.kycField?.name) return { label: kf.kycField.name, fixed: false };
                    if (kf.prefix) return { label: kf.prefix, fixed: true };
                    if (kf.suffix) return { label: kf.suffix, fixed: true };
                    return null;
                  }).filter((s): s is { label: string; fixed: boolean } => s !== null) || [];
                  return (
                    <Typography key={fi} variant="body2" component="div" sx={{ overflowWrap: 'anywhere' }}>
                      <Box component="span" sx={{ fontWeight: 500 }}>{f.fieldName || f.standardFieldName}</Box>
                      {' ← '}
                      {sources.map((s, si) => (
                        <React.Fragment key={si}>
                          {si > 0 && ' + '}
                          <Box
                            component="span"
                            sx={{ fontFamily: theme.custom.text.mono, color: 'text.secondary' }}
                          >
                            {s.fixed ? `"${s.label}"` : s.label}
                          </Box>
                        </React.Fragment>
                      ))}
                      {f.regex && (
                        <Box component="span" sx={{ fontFamily: theme.custom.text.mono, color: 'text.secondary' }}>
                          {' '}/{f.regex}/
                        </Box>
                      )}
                    </Typography>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Card>
  );
};

/* ── The whole summary row ── */

export interface Timings {
  ocr: number;
  gateway: number;
  total: number;
}

interface ResultSummaryProps {
  /** The face photo the document carries, as base64. */
  face?: string;
  /** The holder's name as read. */
  name?: string;
  /** Document type, number and nationality, as read. */
  details: string;
  timings: Timings;
  /** How many raw fields were read; omitted where the view does not count them. */
  fieldCount?: number;
  geo: GeoData | null;
  docTypeCandidates: Array<string | undefined>;
  nationalityCandidates: Array<string | undefined>;
}

/**
 * What a document result says at a glance: who and what was read with how long each hop took,
 * where the holder lives, and which field mappings apply to the document.
 */
export const ResultSummary: React.FC<ResultSummaryProps> = ({
  face, name, details, timings, fieldCount, geo, docTypeCandidates, nationalityCandidates,
}) => {
  const t = useTranslate();
  const theme = useTheme();

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ ...cardSx, p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {face && (
                <Box
                  component="img"
                  src={asImageSrc(face)}
                  alt={t('screens.testPage.facePhoto')}
                  sx={{
                    width: 56, height: 68, objectFit: 'cover', flexShrink: 0,
                    borderRadius: theme.custom.radius.small, border: `1px solid ${theme.palette.divider}`,
                  }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h3" component="p" sx={{ overflowWrap: 'anywhere' }}>{name || t('test.unknown')}</Typography>
                {details && <Typography variant="body2" color="text.secondary">{details}</Typography>}
              </Box>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <MetricRow>
              {timings.ocr > 0 && <Metric label={t('screens.testPage.timeOcr')} value={`${timings.ocr} ms`} />}
              {timings.gateway > 0 && <Metric label={t('screens.testPage.timeGateway')} value={`${timings.gateway} ms`} />}
              {timings.total > 0 && <Metric label={t('screens.testPage.timeTotal')} value={`${timings.total} ms`} />}
              {fieldCount !== undefined && (
                <Metric label={t('screens.testPage.fieldsRead')} value={fieldCount} />
              )}
            </MetricRow>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <GeoCard geo={geo} />
        </Grid>
        <Grid item xs={12} md={4}>
          <DocumentMappingCard docTypeCandidates={docTypeCandidates} nationalityCandidates={nationalityCandidates} />
        </Grid>
      </Grid>
    </Box>
  );
};
